import { useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { intlLocaleForUi } from "@/i18n";
import { useAudienceProfilesHydration } from "@/features/studies/report/hooks/useAudienceProfilesHydration";
import { useStudyReportData } from "@/features/studies/report/hooks/useStudyReportData";
import { useStudyReportDeckHydration } from "@/features/studies/report/hooks/useStudyReportDeckHydration";
import {
  EMPTY_REPORT_RESPONSES,
  getStudyReportName,
  hasStudyReportResponses,
} from "@/features/studies/report/readiness";
import { useParams, useRouter } from "@/routing/navigation";
import { useTwinChatEnabled } from "@/hooks/useTwinChatEnabled";
import Link from "@/routing/Link";
import dynamic from "@/routing/dynamic";
import type { ReportLandingPageProps } from "./components/ReportLandingPage";
import type { ReportWaitingPageProps } from "./components/ReportWaitingPage";
import { ReportPageSkeleton } from "./components/ReportPageSkeleton";
import { PageLayout } from "@/components/layout/PageLayout";
import { Button } from "@/components/ui/button";
import { Heading } from "@/components/ui/Heading";
import { Text } from "@/components/ui/Text";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Loader2, AlertCircle, Zap } from "lucide-react";
import { AnalyticsEvents, track } from "@/lib/services/analytics";
import { ReportDashboard } from "./components/views/overview/ReportDashboard";
import { SurveyFiguresProvider } from "@/features/studies/report/figures/SurveyFiguresProvider";
import { DownloadCsvButton, DownloadXlsxButton } from "./components/DownloadXlsxButton";
import { IncompleteTwinDeliveryNotice } from "./components/IncompleteTwinDeliveryNotice";
import { resolveIncompleteTwinDelivery } from "@/features/studies/incompleteTwinDelivery";
import { Skeleton } from "@/components/ui/skeleton";
import { reportSurveyView } from "@/features/studies/report/utils/reportSurveyView";

const ReportLandingPage = dynamic<ReportLandingPageProps>(
  () =>
    import("./components/ReportLandingPage").then((mod) => ({
      default: mod.ReportLandingPage,
    })),
  {
    loading: () => <ReportPageSkeleton />,
  },
);

const ReportWaitingPage = dynamic<ReportWaitingPageProps>(
  () =>
    import("./components/ReportWaitingPage").then((mod) => ({
      default: mod.ReportWaitingPage,
    })),
  {
    loading: () => (
      <div className="min-h-[40vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    ),
  },
);

export default function ResultsPage() {
  const { t, i18n } = useTranslation("app");
  const twinChatEnabled = useTwinChatEnabled();
  const params = useParams();
  const studyId = params.id as string;

  const wasWaitingForReportRef = useRef(true);
  const deckViewTrackedRef = useRef(false);

  const {
    study,
    results,
    survey,
    loading,
    analysisColumns,
    recommendedAnalysisColumns,
    segmentationMappings,
    constantContext,
    userId,
    organizationToken,
    storageName,
    populationId,
    populationName,
    populationCoverUrl,
    deckStatus,
    isRegeneratingSummary,
    isRerunning,
    readiness,
    loadStudy,
    regenerateSummary,
    rerunStudy,
  } = useStudyReportData({
    studyId,
  });

  const router = useRouter();

  const reportGateReady = useMemo(() => {
    if (loading || !study) return false;
    return readiness.isReady && hasStudyReportResponses(results);
  }, [loading, study, results, readiness]);

  const reportResponses = useMemo(() => {
    if (results && Array.isArray(results.responses)) {
      return results.responses;
    }
    return EMPTY_REPORT_RESPONSES;
  }, [results]);

  const audienceProfilesHydration = useAudienceProfilesHydration({
    studyId,
    survey,
    responses: reportResponses,
    enabled: reportGateReady,
  });

  const deckHydration = useStudyReportDeckHydration({
    studyId,
    study,
    audienceProfiles: audienceProfilesHydration.profiles,
    responses: reportResponses,
    populationId,
  });

  const { deck, deckLoading } = deckHydration;

  useEffect(() => {
    if (!deck || deckViewTrackedRef.current) return;
    deckViewTrackedRef.current = true;
    track(AnalyticsEvents.DECK_AVAILABLE_VIEWED, { experiment_id: studyId });
  }, [deck, studyId]);

  const isWaitingForResultsSync = useMemo(() => {
    if (loading || !study || study.status !== "completed") return false;
    if (!readiness.isReady) return false;
    return !hasStudyReportResponses(results);
  }, [loading, study, results, readiness]);

  useEffect(() => {
    if (!reportGateReady) {
      wasWaitingForReportRef.current = true;
      return;
    }
    if (wasWaitingForReportRef.current) {
      wasWaitingForReportRef.current = false;
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [reportGateReady]);

  const handleRerunFailedStudy = useCallback(async () => {
    const newId = await rerunStudy();
    if (newId) {
      router.push(`/tests/${newId}/results`);
    }
  }, [rerunStudy, router]);

  // Only the quick-study skeleton below consumes this, but it must stay above
  // the early returns: hooks after `if (loading)` run on some renders and not
  // others, which is what "Rendered more hooks than during the previous render"
  // was. The memo already no-ops without a survey, so hoisting costs nothing.
  const skeletonSurveySource = useMemo(
    () => reportSurveyView(study?.surveyJson) || survey,
    [study?.surveyJson, survey],
  );
  const skeletonQuestions = useMemo(() => {
    if (!skeletonSurveySource?.pages) return [];
    const questions: Array<{ name: string; title: string; type: string }> = [];
    for (const page of skeletonSurveySource.pages) {
      for (const el of (page.elements || page.questions || [])) {
        if (el.type === "html" || el.type === "image") continue;
        if (el.type === "panel") {
          for (const sub of (el.elements || [])) {
            if (sub.name && sub.type) questions.push(sub);
          }
        } else if (el.name && el.type) {
          questions.push(el);
        }
      }
    }
    return questions;
  }, [skeletonSurveySource]);

  if (loading) {
    return (
      <div data-testid="test-results-loading">
        <ReportPageSkeleton />
      </div>
    );
  }

  if (!study) {
    return (
      <PageLayout className="max-w-3xl" data-testid="test-results-not-found">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
              <AlertCircle className="h-6 w-6 text-muted-foreground" />
            </div>
            <Heading level={3} className="mb-2">
              {t("studyReport.studyNotFound")}
            </Heading>
            <Text size="sm" color="muted" className="mb-6">
              {t("studyReport.studyNotFoundBody")}
            </Text>
            <Button asChild variant="primary">
              <Link href="/tests">
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t("studyReport.backToStudies")}
              </Link>
            </Button>
          </CardContent>
        </Card>
      </PageLayout>
    );
  }

  const effectiveReadiness = readiness;
  const hasReportData = hasStudyReportResponses(results);
  const canRegenerateSummary = study?.status === "completed";
  const isFailed =
    study?.status === "failed" || study?.status === "cancelled";
  // A run that never reached simulation gave its questionnaire back to the
  // builder; it has nothing to re-run (L21: Retry answered a silent 409).
  const launchReleased = isFailed && study?.launchReleased === true;

  const onRetry = launchReleased
    ? undefined
    : isFailed
      ? handleRerunFailedStudy
      : canRegenerateSummary
      ? regenerateSummary
      : undefined;
  const isRetrying = isFailed ? isRerunning : isRegeneratingSummary;

  // Data mode draws the report's question cards from the figure service
  // (plots W6-R) once the study is completed; a study still collecting shows
  // placeholder cards (the service has nothing to serve yet). This view drew
  // today's charts before, so a card the service cannot draw keeps them.
  const reportDashboard = (
    <ReportDashboard
      survey={survey}
      responses={results?.responses || []}
      populationAttributes={
        recommendedAnalysisColumns.length > 0
          ? recommendedAnalysisColumns
          : analysisColumns
      }
      segmentationMappings={segmentationMappings}
      constantContext={constantContext}
      canonicalAnalysis={study.canonicalAnalysisJson}
      hideHeader
      contentLanguage={study.language ?? undefined}
    />
  );
  const completedDashboard =
    study.status === "completed" ? (
      <SurveyFiguresProvider studyId={studyId} failurePolicy="legacy">
        {reportDashboard}
      </SurveyFiguresProvider>
    ) : (
      reportDashboard
    );

  if (study.studyMode === 'quick' && !isFailed) {
    const isSimulating = study.status !== 'completed' && study.status !== 'failed' && study.status !== 'cancelled';
    const respondentsStarted = (study.respondentsCompleted || 0) > 0;
    const twinShortfall = hasReportData
      ? resolveIncompleteTwinDelivery({
          metadata: results?.metadata,
          completed: study.respondentsCompleted,
          requested: study.numRespondents,
          status: study.status,
        })
      : null;

    const phaseMessage = (() => {
      if (!isSimulating) {
        if (!hasReportData) return t("studyReport.finalizingResults");
        return null;
      }
      if (!respondentsStarted && (study.progressPercent || 0) === 0) return t("studyReport.programmingQuestionnaire");
      if (!respondentsStarted) return t("studyReport.gettingReadyToCollect");
      return null;
    })();

    return (
      <div
        className="mx-auto max-w-4xl px-6 py-10 animate-in fade-in slide-in-from-bottom-2 duration-500"
        data-testid="quick-study-results"
      >
        <div className="mb-10">
          <div className="flex items-center gap-2 mb-3">
            <span
              className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary"
              data-testid="quick-study-badge"
            >
              <Zap className="h-3 w-3" />
              {t("studyReport.dataReport")}
            </span>
            {isSimulating && (
              <span className="ml-auto text-sm text-muted-foreground flex items-center gap-1.5 tabular-nums transition-all duration-500">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                {phaseMessage ? (
                  <span key={phaseMessage} className="animate-in fade-in duration-500">{phaseMessage}</span>
                ) : (
                  <>
                    {t("studyReport.collectingProgress", {
                      completed: study.respondentsCompleted || 0,
                      total: study.numRespondents,
                    })}
                  </>
                )}
              </span>
            )}
            {!isSimulating && !hasReportData && (
              <span className="ml-auto text-sm text-muted-foreground flex items-center gap-1.5">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                {t("studyReport.finalizing")}
              </span>
            )}
            {/* The data is the whole deliverable of a data-mode study, so the
                export lives on the view itself — there is no Survey-data tab
                here to host it. Gated on the run being done: the export
                artifact is written by Argo only after the simulation
                succeeds, so offering it earlier would 404. */}
            {!isSimulating && hasReportData && (
              <div className="ml-auto flex items-center gap-2">
                <DownloadCsvButton
                  studyId={studyId}
                  studyName={getStudyReportName(study)}
                  className="shrink-0 gap-1.5"
                  data-testid="quick-study-download-csv"
                />
                <DownloadXlsxButton
                  studyId={studyId}
                  studyName={getStudyReportName(study)}
                  hasScaleTables={study.hasScaleTables === true}
                  studyWorkbookStatus={study.studyWorkbookStatus}
                  className="shrink-0 gap-1.5"
                  data-testid="quick-study-download-xlsx"
                />
              </div>
            )}
          </div>
          <h1 className="text-3xl font-display font-medium tracking-tight leading-[1.15] text-foreground">
            {getStudyReportName(study)}
          </h1>
          <p className="mt-1.5 text-sm text-muted-foreground">
            {t("studyReport.respondentsMeta", {
              count: study.numRespondents,
              date: new Date(study.createdAt).toLocaleDateString(intlLocaleForUi(i18n.language), { month: "long", year: "numeric" }),
            })}
          </p>
          {twinShortfall && (
            <IncompleteTwinDeliveryNotice delivery={twinShortfall} />
          )}
        </div>
        {/* While twins are still answering, placeholder cards and the counter
            above (owner, W6-R f2): no live charts. The completed study draws
            its cards from the figure service. */}
        {hasReportData && !isSimulating ? (
          completedDashboard
        ) : (
          <div className="space-y-16" data-testid="quick-study-placeholder-cards">
            {skeletonQuestions.map((q, idx) => (
              <div
                key={q.name}
                className="space-y-4 animate-in fade-in duration-500"
                style={{ animationDelay: `${idx * 80}ms`, animationFillMode: 'both' }}
                data-testid={`quick-study-placeholder-card-${idx}`}
              >
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <span className="flex items-center justify-center w-5 h-5 rounded-full bg-muted text-[11px] font-medium text-muted-foreground">
                      {idx + 1}
                    </span>
                    <span className="text-sm font-semibold text-foreground">{q.title || q.name}</span>
                  </div>
                  <span className="text-sm text-muted-foreground/60 ml-7">{t("studyReport.waitingResponses")}</span>
                </div>
                <div className="ml-7 rounded-xl bg-muted/40 p-6 space-y-4">
                  <Skeleton className="h-7 w-3/4 rounded-md" />
                  <Skeleton className="h-7 w-1/2 rounded-md" />
                  <Skeleton className="h-7 w-2/3 rounded-md" />
                  {q.type !== 'text' && q.type !== 'comment' && (
                    <Skeleton className="h-7 w-1/3 rounded-md" />
                  )}
                </div>
              </div>
            ))}
            {skeletonQuestions.length === 0 && (
              <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
                <Loader2 className="h-6 w-6 animate-spin mb-3" />
                <p className="text-sm">{t("studyReport.loadingQuestionnaire")}</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  if (!effectiveReadiness.isReady || !hasReportData) {
    return (
      <ReportWaitingPage
        data-testid="test-results-waiting"
        studyName={getStudyReportName(study)}
        readiness={effectiveReadiness}
        respondentsCompleted={study.respondentsCompleted}
        numRespondents={study.numRespondents}
        progressPercent={study.progressPercent}
        onRetry={onRetry}
        isRetrying={isRetrying}
        launchReleased={launchReleased}
        showBackLink={isFailed}
        backHref="/tests"
        backLabel={t("studyReport.backToStudies")}
        isWaitingForResults={isWaitingForResultsSync}
      />
    );
  }

  if (study.studyMode === 'quick') {
    return (
      <div
        className="mx-auto max-w-4xl px-6 py-10 animate-in fade-in slide-in-from-bottom-2 duration-500"
        data-testid="quick-study-results"
      >
        <div className="mb-10">
          <div className="flex items-center gap-2 mb-3">
            <span
              className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary"
              data-testid="quick-study-badge"
            >
              <Zap className="h-3 w-3" />
              {t("studyReport.dataReport")}
            </span>
          </div>
          <h1 className="text-3xl font-display font-medium tracking-tight leading-[1.15] text-foreground">
            {getStudyReportName(study)}
          </h1>
          <p className="mt-1.5 text-sm text-muted-foreground">
            {t("studyReport.respondentsMeta", {
              count: study.numRespondents,
              date: new Date(study.createdAt).toLocaleDateString(intlLocaleForUi(i18n.language), { month: "long", year: "numeric" }),
            })}
          </p>
        </div>
        {completedDashboard}
      </div>
    );
  }

  return (
    <div
      className="animate-in fade-in slide-in-from-bottom-2 duration-500"
      data-testid="test-results-ready"
    >
      <ReportLandingPage
        studyId={studyId}
        studyName={getStudyReportName(study)}
        createdAt={study.createdAt}
        survey={survey}
        responses={results?.responses || []}
        populationFilter={study.populationFilter}
        appliedSegmentNames={study.appliedSegmentNames}
        executiveSummary={study.summaryJson}
        canonicalAnalysis={study.canonicalAnalysisJson}
        storyline={study.storyline}
        audienceProfiles={audienceProfilesHydration.profiles ?? []}
        audienceProfilesLoading={audienceProfilesHydration.loading}
        coverImageUrl={study.coverImageUrl}
        reportHeadline={study.reportHeadline}
        userId={userId}
        organizationToken={organizationToken}
        storageName={storageName}
        populationId={populationId}
        populationName={populationName}
        populationCoverUrl={populationCoverUrl}
        analysisColumns={
          recommendedAnalysisColumns.length > 0
            ? recommendedAnalysisColumns
            : analysisColumns
        }
        segmentationMappings={segmentationMappings}
        constantContext={constantContext}
        deck={deck}
        isDeckLoading={deckLoading}
        deckStatus={deckStatus}
        deckEnabled={study.deckEnabled}
        twinChatEnabled={study.twinChatEnabled && twinChatEnabled}
        canUseReportAnalyst={study.canUseReportAnalyst === true}
        hasScaleTables={study.hasScaleTables === true}
        studyWorkbookStatus={study.studyWorkbookStatus}
        goalId={study.goalId}
        onLoadDeck={deckHydration.loadDeck}
        onDeckUpdate={deckHydration.setDeck}
        isRegeneratingSummary={isRegeneratingSummary}
        onRegenerateSummary={regenerateSummary}
        reportStatus={study.reportStatus}
        availability={readiness.availability}
        isPublicView={false}
        isShared={study.sharingMode !== undefined && study.sharingMode !== 'private'}
        onSharingChanged={loadStudy}
        experimentLanguage={study.language}
        resultsMetadata={results?.metadata}
        respondentsCompleted={study.respondentsCompleted}
        numRespondents={study.numRespondents}
        studyStatus={study.status}
      />
    </div>
  );
}
